/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHeader,
    IList,
    IOrderHistoryViewProps,
    IOrderInformation,
    ISalesOrder,
    getOrderDetailsPageUrl
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node, Button, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import MsDyn365, { IActionContext } from '@msdyn365-commerce/core';
import { AttributeValue, AttributeValueBase, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { getSalesOrderDetailsBySalesIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/SalesOrdersDataActions.g';
import { currencyFormat, dateFormatByLocale, getOrderStatus } from '../../../utils/helper';
import { SearchOrderHistoryAction, SearchOrderHistoryInput } from '../../../actions/search-order-history.action';
import { IOrderHistoryProps } from '../definition-extensions/order-history.ext.props.autogenerated';

export interface OrderHistoryOrderInfomationItemArr {
    itemIdArr?: any;
    orderDetailsLink?: React.ReactNode;
    isMobile?: boolean;
    order?: any;
    customContext?: any;
    orderShipment?: any;
    orderHistory?: any;
    searchResult?: any;
    salesOrder?: any;
    searchOrder?: any;
    resources?: any;
    sitePath?: string;
    usSiteFlag?: boolean;
}
export interface OrderHistorySearchOrderInfomation {
    orderInformationProps?: any;
    searchOrder?: any;
    isMobile?: boolean;
    customContext?: any;
    resources?: any;
    orderDetailsLink?: any;
    sitePath?: string;
    usSiteFlag?: boolean;
}
export const OrderHistoryOrderInfomation: React.FC<IOrderInformation & OrderHistoryOrderInfomationItemArr> = ({
    orderInformationProps,
    salesId,
    amount,
    channelReferenceId,
    orderDetailsLink,
    isMobile,
    order,
    customContext,
    orderHistory,
    usSiteFlag,
    resources
}) => {
    const enum AttributeKeyName {
        Commission = 'Commission',
        CustomerReference = 'Customer reference',
        RequestADeliveryDate = 'Request a delivery date',
        comments = 'comments',
        estimatedShipDate = 'Estimated ship date'
    }

    function getAttributeValue(AttributeValues?: AttributeValueBase[], type?: string) {
        let textValue;
        AttributeValues?.map((e: AttributeValue) => {
            if (e.Name === type) {
                textValue = e.TextValue;
            } else if (e.Name === type) {
                textValue = e.TextValue;
            }
        });
        return textValue ? textValue : '-';
    }

    const salesOrderBasedOnSalesId = orderHistory?.salesOrders?.filter(
        (saleOrder: { SalesId: any }) => saleOrder.SalesId === order?.SalesId
    );
    const requestedDeliveryDate = new Date(salesOrderBasedOnSalesId[0]?.RequestedDeliveryDate).toLocaleDateString('en-GB');
    const createdDateInGBFormat = new Date(order?.CreatedDateTime).toLocaleDateString('en-GB');
    {
        /* VSI CUSTOMIZATION | DATE FORMAT | START */
    }
    const dateLocale = customContext.app.config.dateFormat;
    const currency = customContext.request.channel?.Currency;
    let requestedDeliveryDateByLocale: string | undefined;
    let createdDateInGBFormatByLocale: string | undefined;
    if (dateLocale && currency) {
        requestedDeliveryDateByLocale =
            (requestedDeliveryDate &&
                requestedDeliveryDate.toString() !== 'Invalid Date' &&
                dateFormatByLocale(dateLocale, currency, requestedDeliveryDate)) ||
            undefined;
        createdDateInGBFormatByLocale =
            (createdDateInGBFormat &&
                createdDateInGBFormat.toString() !== 'Invalid Date' &&
                dateFormatByLocale(dateLocale, currency, createdDateInGBFormat)) ||
            undefined;
    }
    {
        /* VSI CUSTOMIZATION | DATE FORMAT | END */
    }
    if (isMobile) {
        return (
            <Node {...orderInformationProps}>
                <div className='order-history-mob-data'>
                    {salesId && (
                        <span className='sales-id-container'>
                            {resources.salesOrderLable}: <span>{salesId}</span>
                        </span>
                    )}
                    {channelReferenceId && (
                        <span className='refrence-container'>
                            <span>{channelReferenceId ? channelReferenceId : '-'}</span>
                        </span>
                    )}
                    {order && order?.AttributeValues && !usSiteFlag && (
                        <span className='commission-container'>
                            {resources.siteNameLable}: <span>{getAttributeValue(order?.AttributeValues, AttributeKeyName.Commission)}</span>
                        </span>
                    )}
                    {order && order?.AttributeValues && (
                        <span className='refrence-container'>
                            {resources.customerRefNoLable}:{' '}
                            <span>{getAttributeValue(order?.AttributeValues, AttributeKeyName.CustomerReference)}</span>
                        </span>
                    )}

                    {createdDateInGBFormatByLocale && (
                        <span className='date-container'>
                            {resources.orderDateLable}: <span>{createdDateInGBFormatByLocale ? createdDateInGBFormatByLocale : '-'}</span>
                        </span>
                    )}
                    {
                        <span className='delivery-date-container'>
                            {resources.estShipDateLable}: <span>{requestedDeliveryDateByLocale ? requestedDeliveryDateByLocale : '-'}</span>
                        </span>
                    }
                    {
                        <span className='net-amount-container'>
                            {resources.netAmountLable}:{' '}
                            <span>
                                {' '}
                                {order?.NetAmountWithNoTax !== 0
                                    ? currencyFormat(Number(order?.NetAmountWithNoTax), customContext)
                                    : currencyFormat(Number(order?.GrossAmount), customContext)}
                            </span>
                        </span>
                    }
                    {amount && (
                        <span className='amount-container'>
                            {resources.totalAmountLable}: <span>{amount}</span>{' '}
                        </span>
                    )}
                    {
                        <span className='currency-container'>
                            {resources.currencyLable}: {usSiteFlag ? <span>$</span> : <span>A$</span>}{' '}
                        </span>
                    }
                    {order && order?.StatusValue && (
                        <span className='status-container'>
                            {resources.statusLable}: <span> {getOrderStatus(order?.StatusValue)}</span>
                        </span>
                    )}
                </div>
            </Node>
        );
    } else {
        return (
            <tr className='ms-order-history__order-information'>
                <td>{salesId ? salesId : '-'}</td>
                <td>{channelReferenceId}</td>
                {!usSiteFlag && <td>{getAttributeValue(order?.AttributeValues, AttributeKeyName.Commission)}</td>}
                <td>{getAttributeValue(order?.AttributeValues, AttributeKeyName.CustomerReference)}</td>
                {/* VSI CUSTOMIZATION | DATE FORMAT | START */}
                <td>{createdDateInGBFormatByLocale ? createdDateInGBFormatByLocale : '-'}</td>
                <td>{requestedDeliveryDateByLocale ? requestedDeliveryDateByLocale : '-'}</td>
                {/* VSI CUSTOMIZATION | DATE FORMAT | END */}
                <td>
                    {order?.NetAmountWithNoTax
                        ? order?.NetAmountWithNoTax !== 0
                            ? currencyFormat(Number(order?.NetAmountWithNoTax), customContext)
                            : currencyFormat(Number(order?.GrossAmount), customContext)
                        : '-'}
                </td>
                <td>{amount}</td>
                {usSiteFlag ? <td>$</td> : <td>A$</td>}
                <td> {getOrderStatus(order?.StatusValue)}</td>
                <td> {orderDetailsLink}</td>
            </tr>
        );
    }
};
export const OrderHistorySearchOrderInfomation: React.FC<OrderHistorySearchOrderInfomation> = ({
    searchOrder,
    isMobile,
    customContext,
    resources,
    sitePath,
    usSiteFlag
}) => {
    const requestedDeliveryDate = new Date(searchOrder?.RequestedDeliveryDate).toLocaleDateString('en-GB');
    const createdDateInGBFormat = new Date(searchOrder?.CreatedDateTime).toLocaleDateString('en-GB');
    const siteName = searchOrder?.ExtensionProperties.find((item: { Key: string }) => item.Key === 'SiteName')?.Value.StringValue;
    const customerRefNo = searchOrder?.ExtensionProperties.find((item: { Key: string }) => item.Key === 'CustomerRefNo')?.Value.StringValue;
    const estimatedShipDate = searchOrder?.ExtensionProperties.find((item: { key: string }) => item.key === 'EstimatedShipDate')?.Value
        ?.StringValue;
    {
        /* VSI CUSTOMIZATION | DATE FORMAT | START */
    }
    const dateLocale = customContext.app.config.dateFormat;
    const currency = customContext.request.channel?.Currency;
    let requestedDeliveryDateByLocale: string | undefined;
    let createdDateInGBFormatByLocale: string | undefined;
    let estimatedShipDateByLocale: string | undefined;
    if (dateLocale && currency) {
        requestedDeliveryDateByLocale =
            (requestedDeliveryDate &&
                requestedDeliveryDate.toString() !== 'Invalid Date' &&
                dateFormatByLocale(dateLocale, currency, requestedDeliveryDate)) ||
            undefined;
        createdDateInGBFormatByLocale =
            (createdDateInGBFormat &&
                createdDateInGBFormat.toString() !== 'Invalid Date' &&
                dateFormatByLocale(dateLocale, currency, createdDateInGBFormat)) ||
            undefined;
        estimatedShipDateByLocale =
            (estimatedShipDate &&
                estimatedShipDate.toString() !== 'Invalid Date' &&
                dateFormatByLocale(dateLocale, currency, estimatedShipDate)) ||
            undefined;
    }
    {
        /* VSI CUSTOMIZATION | DATE FORMAT | END */
    }
    let orderDetailsUrl = getOrderDetailsPageUrl(customContext, searchOrder);
    if (orderDetailsUrl.includes('undefined')) {
        orderDetailsUrl = orderDetailsUrl.replace('undefined', searchOrder.Id);
    }
    let linkToDetailPage = String(sitePath?.concat(orderDetailsUrl));
    linkToDetailPage = linkToDetailPage.replace('orderDetails', 'orderdetails');

    const orderDetailsLinkButton = (
        <Button
            href={linkToDetailPage}
            role='button'
            aria-label={resources?.viewOrderDetailsLable}
            className='ms-order-history__btn-order-details'
        >
            {resources.viewOrderDetailsLable}
        </Button>
    );
    if (isMobile) {
        return (
            <div className='ms-order-history__order-information'>
                <div className='order-history-mob-data'>
                    {searchOrder?.SalesId && (
                        <span className='sales-id-container'>
                            {resources.salesOrderLable}: <span>{searchOrder?.SalesId}</span>
                        </span>
                    )}
                    {searchOrder?.ChannelReferenceId && (
                        <span className='refrence-container'>
                            {resources.confirmationIdText}:{' '}
                            <span>{searchOrder?.ChannelReferenceId ? searchOrder?.ChannelReferenceId : '-'}</span>
                        </span>
                    )}
                    {searchOrder && searchOrder?.ExtensionProperties && (
                        <span className='commission-container'>
                            {resources.siteNameLable}: <span>{siteName ? siteName : '-'}</span>
                        </span>
                    )}
                    {searchOrder && searchOrder?.ExtensionProperties && (
                        <span className='refrence-container'>
                            {resources.customerRefNoLable}: <span>{customerRefNo ? customerRefNo : '-'}</span>
                        </span>
                    )}

                    {/* VSI CUSTOMIZATION | DATE FORMAT | START */}
                    {createdDateInGBFormatByLocale && (
                        <span className='date-container'>
                            {resources.orderDateLable}: <span>{createdDateInGBFormatByLocale ? createdDateInGBFormatByLocale : '-'}</span>
                        </span>
                    )}
                    {
                        <span className='delivery-date-container'>
                            {resources.estShipDateLable}: <span>{estimatedShipDateByLocale ? estimatedShipDateByLocale : '-'}</span>
                        </span>
                    }
                    {/* VSI CUSTOMIZATION | DATE FORMAT | START */}
                    {
                        <span className='net-amount-container'>
                            {resources.netAmountLable}:{' '}
                            <span>
                                {' '}
                                {searchOrder?.NetAmount && searchOrder?.NetAmount !== 0
                                    ? currencyFormat(Number(searchOrder?.NetAmount), customContext)
                                    : currencyFormat(Number(searchOrder?.GrossAmount) - Number(searchOrder?.TaxAmount), customContext)}
                            </span>
                        </span>
                    }
                    {searchOrder && (
                        <span className='amount-container'>
                            {resources.totalAmountLable}: <span>{currencyFormat(Number(searchOrder?.TotalAmount), customContext)}</span>{' '}
                        </span>
                    )}
                    {
                        <span className='currency-container'>
                            {resources.currencyLable}: {usSiteFlag ? <span>$</span> : <span>A$</span>}{' '}
                        </span>
                    }
                    {searchOrder && searchOrder?.StatusValue && (
                        <span className='status-container'>
                            {resources.statusLable}: <span> {getOrderStatus(searchOrder?.StatusValue)}</span>
                        </span>
                    )}
                    {orderDetailsLinkButton && orderDetailsLinkButton}
                </div>
            </div>
        );
    } else {
        return (
            <tr className='ms-order-history__order-information'>
                <td>{searchOrder?.SalesId ? resources.orderNumLable + searchOrder?.SalesId : '-'}</td>
                <td>{searchOrder?.ChannelReferenceId}</td>
                {usSiteFlag ? null : <td>{siteName}</td>}
                <td>{customerRefNo ? customerRefNo : '-'}</td>
                {/* VSI CUSTOMIZATION | DATE FORMAT | START */}
                <td>{createdDateInGBFormatByLocale ? createdDateInGBFormatByLocale : '-'}</td>
                <td>{requestedDeliveryDateByLocale ? requestedDeliveryDateByLocale : '-'}</td>
                {/* VSI CUSTOMIZATION | DATE FORMAT | END */}
                <td>
                    {searchOrder?.NetAmount !== 0
                        ? currencyFormat(Number(searchOrder?.NetAmount), customContext)
                        : currencyFormat(Number(searchOrder?.GrossAmount) - Number(searchOrder?.TaxAmount), customContext)}
                </td>
                <td>{currencyFormat(Number(searchOrder?.TotalAmount), customContext)}</td>
                {usSiteFlag ? <td>$</td> : <td>A$</td>}
                <td> {getOrderStatus(searchOrder?.StatusValue)}</td>
                <td> {orderDetailsLinkButton}</td>
            </tr>
        );
    }
};

export const OrderHistoryGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    trackingInfo
}) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {processing}
        {address}
        {trackingInfo}
    </Node>
);

export const OrderHistoryGroup: React.FC<IGroup> = ({ groupProps, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>{salesLine.salesLine}</React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);

export const OrderHistoryGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderHistoryGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderHistoryHeader: React.FC<IHeader> = ({ headerProps, heading }) => <Node {...headerProps}>{heading}</Node>;

export const OrderHistorySalesOder: React.FC<ISalesOrder & OrderHistoryOrderInfomationItemArr & any> = ({
    salesOrderProps,
    orderInfomation,
    salesOrder,
    orderDetailsLink,
    expandProductsButton,
    isMobile,
    customContext,
    searchOrder,
    orderHistory,
    resources,
    sitePath,
    usSiteFlag
}) => {
    const orderNo = orderInfomation?.salesId?.props?.children.substr(7);
    const [order, setOrder] = React.useState<SalesOrder>();
    React.useEffect(() => {
        getSalesOrder(orderNo)(customContext.actionContext).then((res: any) => {
            setOrder(res);
        });
    }, []);

    const getSalesOrder = (orderId: string = '') => async (ctx: IActionContext): Promise<SalesOrder | undefined> => {
        return getSalesOrderDetailsBySalesIdAsync({ callerContext: ctx }, orderId);
    };
    if (isMobile) {
        if (searchOrder) {
            return (
                <div className='ms-order-history__sales-order'>
                    {searchOrder && (
                        <OrderHistorySearchOrderInfomation
                            searchOrder={searchOrder}
                            isMobile={isMobile}
                            customContext={customContext}
                            {...salesOrderProps}
                            orderDetailsLink={orderDetailsLink}
                            sitePath={sitePath}
                            usSiteFlag={usSiteFlag}
                            resources={resources}
                        />
                    )}
                    {expandProductsButton}
                    {orderDetailsLink}
                </div>
            );
        }
        return (
            <Node {...salesOrderProps}>
                {orderInfomation && (
                    <OrderHistoryOrderInfomation
                        {...orderInfomation}
                        orderHistory={orderHistory}
                        isMobile={isMobile}
                        order={order}
                        orderDetailsLink={orderDetailsLink}
                        customContext={customContext}
                        salesOrder={salesOrder}
                        usSiteFlag={usSiteFlag}
                        resources={resources}
                    />
                )}
                {expandProductsButton}
                {orderDetailsLink}
            </Node>
        );
    } else {
        if (searchOrder) {
            return (
                searchOrder && (
                    <OrderHistorySearchOrderInfomation
                        searchOrder={searchOrder}
                        isMobile={isMobile}
                        customContext={customContext}
                        resources={resources}
                        orderDetailsLink={orderDetailsLink}
                        sitePath={sitePath}
                        usSiteFlag={usSiteFlag}
                    />
                )
            );
        }
        return (
            orderInfomation && (
                <OrderHistoryOrderInfomation
                    {...orderInfomation}
                    orderHistory={orderHistory}
                    orderDetailsLink={orderDetailsLink}
                    isMobile={isMobile}
                    order={order}
                    customContext={customContext}
                    salesOrder={salesOrder}
                    usSiteFlag={usSiteFlag}
                />
            )
        );
    }
};

export const OrderHistoryList: React.FC<IList & OrderHistoryOrderInfomationItemArr> = ({
    listProps,
    salesOrders,
    isMobile,
    customContext,
    orderHistory,
    searchResult,
    resources,
    sitePath,
    usSiteFlag
}) => {
    if (searchResult && searchResult.length === 0) {
        return (
            <Node {...listProps}>
                <h3 className='no-results'>{resources.noResultFoundText}</h3>
            </Node>
        );
    }
    if (isMobile) {
        if (searchResult && searchResult.length > 0) {
            return (
                <Node {...listProps}>
                    {searchResult &&
                        searchResult.map((searchOrder: any, index: number) => (
                            <OrderHistorySalesOder
                                key={index}
                                searchOrder={searchOrder}
                                isMobile={isMobile}
                                customContext={customContext}
                                sitePath={sitePath}
                                usSiteFlag={usSiteFlag}
                                resources={resources}
                            />
                        ))}
                </Node>
            );
        }
        return (
            <Node {...listProps}>
                {salesOrders &&
                    salesOrders.map((salesOrder: ISalesOrder, index: number) => (
                        <OrderHistorySalesOder
                            key={index}
                            {...salesOrder}
                            isMobile={isMobile}
                            customContext={customContext}
                            orderHistory={orderHistory}
                            usSiteFlag={usSiteFlag}
                            resources={resources}
                        />
                    ))}
            </Node>
        );
    }
    const tableHeadings = (
        <tr>
            <th>{resources.salesOrderLable}</th>
            <th>{resources.confirmationIdText}</th>
            {!usSiteFlag && <th>{resources.siteNameLable}</th>}
            <th>{resources.customerRefNoLable}</th>
            <th>{resources.orderDateLable}</th>
            <th>{resources.estShipDateLable}</th>
            <th>{resources.netAmountLable}</th>
            <th>{resources.totalAmountLable}</th>
            <th>{resources.currencyLable}</th>
            <th>{resources.statusLable}</th>
            <th>{resources.actionLable}</th>
        </tr>
    );
    if (searchResult && searchResult.length > 0 && !isMobile) {
        return (
            <Node {...listProps}>
                <table>
                    {tableHeadings}
                    {searchResult &&
                        searchResult.map((searchOrder: any, index: any) => (
                            <OrderHistorySalesOder
                                key={index}
                                searchOrder={searchOrder}
                                isMobile={isMobile}
                                customContext={customContext}
                                sitePath={sitePath}
                                usSiteFlag={usSiteFlag}
                                resources={resources}
                            />
                        ))}
                </table>
            </Node>
        );
    }
    return (
        <Node {...listProps}>
            <table>
                {tableHeadings}
                {salesOrders &&
                    salesOrders.map((salesOrder, index) => (
                        <OrderHistorySalesOder
                            key={index}
                            {...salesOrder}
                            isMobile={isMobile}
                            customContext={customContext}
                            orderHistory={orderHistory}
                            usSiteFlag={usSiteFlag}
                            resources={resources}
                        />
                    ))}
            </table>
        </Node>
    );
};

const OrderHistoryView: React.FC<IOrderHistoryViewProps & IOrderHistoryProps<{}>> = props => {
    const {
        orderHistoryProps,
        orderHistory,
        header,
        alert,
        loading,
        emptyMessage,
        backToShoppingLink,
        list,
        table,
        moreButton,
        context,
        resources
    } = props;
    const {
        orderStatusText,
        confirmationIdText,
        salesIdText,
        searchCriteriaText,
        confirmationIdPlaceHolder,
        salesIdPlaceHolder
    } = resources;
    type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    const [isMobile, setIsMobile] = React.useState<boolean>(false);
    const [res, setRes] = React.useState<any>();
    const [searchText, setSearchText] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState(false);
    const customer = props.data.customerInformation.result;
    const orderStatusOption = orderStatusText;
    const searchCriteriaDropdownValues = [
        {
            value: salesIdText
        },
        {
            value: confirmationIdText
        },
        {
            value: orderStatusText
        }
    ];
    const orderStatusDropdownValues = [
        {
            value: 'Open Order',
            key: '0',
            isDisabled: true
        },
        {
            value: 'Processing',
            key: '1',
            isDisabled: false
        },
        {
            value: 'Shipped',
            key: '3',
            isDisabled: false
        },
        {
            value: 'Invoiced',
            key: '4',
            isDisabled: false
        }
    ];
    const [selectedOption, setSelectedOption] = React.useState(searchCriteriaDropdownValues[0].value || 'ChannelReferenceId');
    const [selectedStatus, setSelectedStatus] = React.useState('0');
    const _updateViewport = (): void => {
        const viewport = _getViewport();
        viewport === 'xs' || viewport === 'sm' ? setIsMobile(true) : setIsMobile(false);
    };

    const _getViewport = (): GridSize => {
        const { context } = props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'lg';
    };
    React.useEffect(() => {
        _updateViewport();
        if (MsDyn365.isBrowser && window.addEventListener) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            window.addEventListener('resize', _updateViewport);
        }
    }, []);

    const handleOnSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            setIsLoading(true);
            const searchOrderInput = new SearchOrderHistoryInput(
                {
                    key: selectedOption,
                    searchTerm:
                        selectedOption === orderStatusOption
                            ? (orderStatusDropdownValues.find(item => item.value === selectedStatus)?.key as string) || '1'
                            : searchText,
                    accountNumber: customer?.AccountNumber
                },
                context.request.apiSettings,
                context
            );

            const response = await SearchOrderHistoryAction(searchOrderInput, context.actionContext);
            setRes(response);
        } catch (error) {
            setRes(undefined);
            return error;
        } finally {
            setIsLoading(false);
        }
        return;
    };

    return (
        <Module {...orderHistoryProps}>
            {header && <OrderHistoryHeader {...header} />}
            {
                <div className='search-panel'>
                    <h3>{searchCriteriaText}</h3>
                    <div className='order-search-form'>
                        <select
                            aria-label='search filter'
                            value={selectedOption}
                            onChange={e => {
                                setSelectedOption(e.target.value);
                                setSearchText('');
                            }}
                        >
                            {searchCriteriaDropdownValues.map(item => {
                                return <option value={item.value}>{item.value}</option>;
                            })}
                        </select>
                        <form onSubmit={handleOnSubmit} className='search-form__order-history'>
                            {selectedOption === orderStatusText ? (
                                <select
                                    value={selectedStatus}
                                    onChange={e => {
                                        setSelectedStatus(e.target.value);
                                    }}
                                >
                                    {orderStatusDropdownValues.map(item => {
                                        return (
                                            <option value={item.value} disabled={item.isDisabled}>
                                                {item.value}
                                            </option>
                                        );
                                    })}
                                </select>
                            ) : (
                                <>
                                    <label className='sr-only' htmlFor={selectedOption}>
                                        {selectedOption}
                                    </label>
                                    <input
                                        id={selectedOption}
                                        placeholder={selectedOption === confirmationIdText ? confirmationIdPlaceHolder : salesIdPlaceHolder}
                                        value={searchText}
                                        onChange={e => setSearchText(e.target.value)}
                                    ></input>
                                </>
                            )}
                            <button>{resources.searchButtonText}</button>
                        </form>
                    </div>
                </div>
            }
            {loading && <Waiting className='msc-waiting-circular msc-waiting-lg' />}
            {alert && (
                <>
                    {alert}
                    {backToShoppingLink}
                </>
            )}
            {emptyMessage && (
                <Node className='ms-order-history__empty-message-container'>
                    {emptyMessage}
                    {backToShoppingLink}
                </Node>
            )}
            {isLoading && <Waiting className='msc-waiting-circular msc-waiting-lg' />}
            {list && !isLoading && (
                <OrderHistoryList
                    {...list}
                    isMobile={isMobile}
                    customContext={props.context}
                    orderHistory={orderHistory}
                    searchResult={res ? res.salesOrders : undefined}
                    resources={resources}
                    sitePath={props.context.request.sitePath}
                    usSiteFlag={
                        props?.context?.actionContext?.requestContext?.apiSettings?.oun === props.context.app.config.ounNumberForUsSite
                    }
                />
            )}
            {table}
            {!isLoading && !res && moreButton && moreButton}
        </Module>
    );
};
export default OrderHistoryView;
